import React, { useEffect, useMemo, useState } from 'react'
import { DadosBorderoDup, bgLineGridColorDark, bgLineGridColorLight, corBgGrid } from '../../Utils/Types'
import { createTheme, ThemeProvider } from '@mui/material'
import { useSelector } from 'react-redux'
import { selectUser } from '../Redux/userSlice'
import { MRT_ColumnDef, MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR'
import moment from 'moment'
import { Funcoes } from '../../Utils/Funcoes'
import { Tooltip } from 'react-tooltip'


type Props = {
    duplicata: DadosBorderoDup[],
    handleSelectItemClick: (id: number, selected: boolean) => void,
}

interface RowData {
    valor: number,
    id_bordero: number,
    dt_liquidacao: string,
    obs_liquidacao: string,
    selected: boolean,
    qtd: number,
}

export const GridContaAPagarEstorno = React.memo(({ duplicata, handleSelectItemClick }: Props) => {

    const [state, setState] = useState(false)
    const [duplicatas, setDuplicatas] = useState<DadosBorderoDup[]>([])

    let user = useSelector(state => selectUser(state))

    //---------------------------------------------------------------------------------------------------------------------------------
    // ----------------------------------------------------------- EFFECT -------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------

    useEffect(() => {
        setDuplicatas(duplicata)
    }, [duplicata])

    //---------------------------------------------------------------------------------------------------------------------------------
    // ----------------------------------------------------------- TEMAS --------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------
    const theme = createTheme({
        palette: {
            mode: user.modeDark,
            background: {
                default: user.modeDark === 'light' ? '#fff' : '#475569',
            },
        },
    })

    const baseBackgroundColor = user.modeDark === 'dark' ? '#475569' : '#fff'


    //---------------------------------------------------------------------------------------------------------------------------------
    // ----------------------------------------------------------- COLUNAS ------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------
    const handleCheckBoxClick = (id: number, selected: boolean) => {

        setState(!state)
        handleSelectItemClick(id, selected)

    }

    const columns = useMemo<MRT_ColumnDef<DadosBorderoDup>[]>(() => [

        {
            accessorKey: 'estornar', header: '[Estornar]', size: 150, visibleInShowHideMenu: false, muiTableBodyCellProps: { align: 'center' }, muiTableHeadCellProps: { align: 'center' },
            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (

                    <div className={`w-full h-[30px]`} >

                        <input
                            id={'checkbox-' + rowData.id_bordero}
                            aria-label={rowData.id_bordero.toString()}
                            type='checkbox'
                            className={`w-5 h-5 mx-2 cursor-pointer`}
                            onChange={() => handleCheckBoxClick(rowData.id_bordero, rowData.selected)}
                            checked={!!rowData.selected}
                        />

                        <Tooltip style={{ backgroundColor: 'rgb(30, 41, 59)', zIndex: 9999 }} id={'tooltip-checkbox-' + rowData.id_bordero} anchorSelect={'#checkbox-' + rowData.id_bordero} positionStrategy='fixed' >
                            <span >
                                Selecione para <br />
                                Estornar
                            </span>
                        </Tooltip>

                    </div>
                )
            }
        },

        {
            accessorKey: 'obs_liquidacao', header: 'Observação', visibleInShowHideMenu: false, size: 250, muiTableBodyCellProps: { align: 'left' }, muiTableHeadCellProps: { align: 'left' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.obs_liquidacao ? rowData.obs_liquidacao : <span className={`text-red-400`}>N/D</span>}</span>)

            }
        },

        {
            accessorKey: 'dt_liquidacao', header: 'Data Liquidação', visibleInShowHideMenu: false, size: 50, muiTableBodyCellProps: { align: 'center' }, muiTableHeadCellProps: { align: 'center' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.dt_liquidacao ? moment(rowData.dt_liquidacao).format('DD/MM/YYYY') : <span className={`text-red-400`}>N/D</span>}</span>)

            }
        },

        {
            accessorKey: 'valor', visibleInShowHideMenu: true, header: 'Valor (R$)', size: 100, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.valor ? Funcoes.numberFormat(rowData.valor, 'currency') : <span className={`text-red-400`}>N/D</span>}</span>)

            }
        },

        {
            accessorKey: 'qtd', header: 'Qtde de duplicatas', visibleInShowHideMenu: false, size: 100, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.qtd ? rowData.qtd : <span className={`text-red-400`}>N/D</span>}</span>)

            }
        },

        {
            accessorKey: 'id_bordero', header: 'Bordero', visibleInShowHideMenu: false, size: 50, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.id_bordero ? rowData.id_bordero : <span className={`text-red-400`}>N/D</span>}</span>)

            }
        },

    ], [duplicata]
    )


    //---------------------------------------------------------------------------------------------------------------------------------
    // ----------------------------------------------------------- TABLE --------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------
    const table = useMaterialReactTable({
        paginationDisplayMode: 'pages',
        columns: columns,
        data: duplicatas,
        autoResetPageIndex: false,
        mrtTheme: (theme) => ({
            baseBackgroundColor: baseBackgroundColor,
        }),
        muiTableHeadCellProps: {
            sx: {
                borderBottom: `0.1px solid ${user.modeDark === 'light' ? bgLineGridColorLight : bgLineGridColorDark}`,
                backgroundColor: corBgGrid,
                color: `#fff`,
                '& .MuiTableSortLabel-root': {
                    color: '#fff !important',
                    '&:hover': {
                        color: '#fff !important',
                    },
                    '&.Mui-active': {
                        color: '#fff !important',
                    },
                    '& .MuiTableSortLabel-icon': {
                        color: '#fff !important',
                    },
                    '&.Mui-active .MuiTableSortLabel-icon': {
                        color: '#fff !important',
                    },
                },
                '& .MuiIconButton-root': {
                    color: '#fff',
                    '&:hover': {
                        color: '#fff',
                    },
                },
            },
        },
        muiTableBodyCellProps: {
            sx: {
                borderBottom: `0.1px solid ${user.modeDark === 'light' ? bgLineGridColorLight : bgLineGridColorDark}`
            }
        },
        localization: MRT_Localization_PT_BR,
        enableEditing: false,
        enableTopToolbar: false,
        enableColumnActions: false,
        enableColumnFilters: false,
        enableHiding: false,
        enableFilters: false,
        enableMultiSort: false,
        enableSorting: false,
        enableFullScreenToggle: false,
        enableDensityToggle: false,
    })

    return (
        <>

            <ThemeProvider theme={theme}>
                <MaterialReactTable table={table} />
            </ThemeProvider>

        </>
    )

})